import React, {FC, Children} from "react"
import styled, {css} from "styled-components"
import {minWidthMedia, PHONE_LANDSCAPE, DESKTOP} from "../global/media"

export interface GridCenteredProps {
  maxColumns?: 2
}

interface GridProps extends GridCenteredProps {
  count: number
}

const twoColumnLayout = css<GridProps>`
  ${({count}) =>
    count > 2 &&
    css`
      & > *:last-child:nth-child(2n - 1) {
        grid-column-end: 4;
      }
    `}
`

const threeColumnLayout = css<GridProps>`
  grid-template-columns: repeat(6, 1fr);

  /* Dealing with a partial last row if more than three in grid */
  ${({count}) =>
    count > 3 &&
    css`
      /* Dealing with the last two elements */
      & > *:last-child:nth-child(3n - 1) {
        grid-column-end: -2;
      }

      & > *:nth-last-child(2):nth-child(3n + 1) {
        grid-column-end: 4;
      }

      /* Dealing with single in last row */
      & > *:last-child:nth-child(3n - 2) {
        grid-column-end: 5;
      }
    `}
`

/**
 * Layout a list of items in an evenly spaced grid, where the last row will be centered
 * if you don't need a centered row, DON'T USE THIS, favour using something like
 * ```
 * grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
 * ```
 *
 * The intention is to use this with a list of fixed width items. The number of columns can be adjusted to hopefully fit the items better
 *
 * e.g
 * ```
 * | item | item | item |
 * | item | item | item |
 * |      | item |      |
 *
 * | item | item | item |
 * | item | item | item |
 *    | item | item  |
 *
 * | item | item |
 * | item | item |
 *    | item |
 * ```
 *
 * at time of writing, there is no need to support more than 3 columns, however there's no reason why it couldn't be extended
 * alternatively, clever use of container queries could likely be used, but it's still an early feature
 */
const Grid = styled.div<GridProps>`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  gap: ${({theme}) => theme.spacing.two};

  /* for one item on a larger screen, we want to center, but limit the width */
  ${({count}) =>
    count === 1 &&
    minWidthMedia.phoneLandscape<GridProps>`
      flex-direction: row;
      justify-content: center;

      & > * {
          /* this is arbitrary but looks nice */
          flex-basis: 300px;
      }
    `}

  /* if there is more than one child, we can consider more complex layouts */
    ${({count}) =>
    count > 1 &&
    css`
      display: grid;
      & > * {
        grid-column: span 1;
      }

      /* Once we hit a large enough size, we allow two columns. We use four grid fractions to allow a single item on the bottom row to be center aligned */
      ${minWidthMedia.phoneLandscape<GridProps>`
        grid-template-columns: repeat(4, 1fr);
        & > * {
            grid-column: span 2;
        }
      `}

      /* this should only be applied between this and the next breakpoint to prevent the layout rules clashing */
      @media (min-width: ${PHONE_LANDSCAPE}px) and (max-width: ${DESKTOP - 1}px) {
        ${twoColumnLayout}
      }

      /* Finally we allow for a threeColumnLayout, however the caller can limit it to two columns if the content won't fit well .*/
      ${minWidthMedia.desktop<GridProps>`
        ${({maxColumns = 3}) => (maxColumns > 2 && count > 2 ? threeColumnLayout : twoColumnLayout)}
      `}
    `}
`

const GridCentered: FC<GridCenteredProps> = ({children, maxColumns}) => (
  <Grid maxColumns={maxColumns} count={Children.count(children)}>
    {children}
  </Grid>
)
export default GridCentered
